import { AxiosResponse } from 'axios';
import { Model, Requestor, Request } from '@viddsee/server-api-client';

import { commonApi } from '@/scripts/webcall/v3/common';
import { identifiers as channelIdentifierDefinitions } from '@/scripts/models/videoCollections/identifierDefinitions/channel';
import { CollectionImplementation, CollectionImplementationData, CollectionImplementationOptions } from './core/BaseCollectionImplementation';

export class ChannelCollectionImplementation extends CollectionImplementation {
  constructor({ video, params }: CollectionImplementationOptions) {
    super({
      video,
      params,
      identifierDefinitions: channelIdentifierDefinitions,
    });
  }

  test(): boolean {
    return !!this.params.channel;
  }

  provide(): CollectionImplementationData {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const implementation = this;

    return new CollectionImplementationData({
      implementation,
      fetchers: {
        async info(): Promise<Model.VideoCollection.Info> {
          const webcalls = implementation.identifierValues
            .map((_identifier, index, identifierValues) => {
              const paths = identifierValues.slice(0, index + 1);
              return `/${(
                paths
                  .map(({ ids, value }) => [ids.apiId, value].join('/'))
                  .join('/')
              )}`;
            })
            .filter(x => x)
            .map(infoUrl => (
              commonApi.get({ url: infoUrl }) as Promise<AxiosResponse<Model.VideoCollection.Info>>
            )) as Promise<AxiosResponse<Model.VideoCollection.Info>>[]
          ;

          const infos = await Promise.all(webcalls).then(responses => responses.map(({ data }) => data));
          const displayTitle = infos
            .map(({ title }) => title || '')
            .filter(x => x)
            .reduce((acc, title, index, titles) => {
              const nextTitle = titles[index + 1] || '';
              if (nextTitle.includes(title)) {
                return acc;
              }
              acc.push(title.trim());
              return acc;
            }, [] as string[])
            .filter(x => x)
            .join(': ')
          ;

          return ({
            ...infos[infos.length - 1],
            ...[...infos]
              .reverse()
              .reduce((acc, info) => ({
                ...acc,
                description: acc.description || info.description,
                photoLargeUrl: acc.photoLargeUrl || info.photoLargeUrl,
                photoMediumUrl: acc.photoMediumUrl || info.photoMediumUrl,
                photoSmallUrl: acc.photoSmallUrl || info.photoSmallUrl,
                type: acc.type || info.type,
              }), {} as Model.VideoCollection.Info)
            ,
            title: displayTitle,
          });
        },
        async videoIndex(params): Promise<number> {
          if (!implementation.video) return -1;

          try {
            const { data: collectionIndexResponse } = await commonApi.get({ url: `${implementation.collectionApiRequestPath}/video_index` }, {
              videoId: implementation.video.id,
              ...params,
            }) as Requestor.ResponseWrapper<Model.Video.FindCollectionIndexResponse>;
            return collectionIndexResponse.index;
          } catch (error) {
            return -1;
          }
        },
        async videos(params: Request.Pagination.Options = {}): Promise<Model.Pagination.PaginatedItemsResponse<Model.Video.Info>> {
          const { data: PaginatedVideoInfoResponse } = await commonApi.get({ url: `${implementation.collectionApiRequestPath}/videos` }, {
            order: Request.Pagination.OptionOrder.Ascending,
            ...params,
          }) as Requestor.ResponseWrapper<Model.Pagination.PaginatedItemsResponse<Model.Video.Info>>;
          return PaginatedVideoInfoResponse;
        },
      },
    });
  }
}
